import { Model } from "appworks/model/model";
import { contains, lowerCaseMapKeys } from "appworks/utils/collection-utils";
import { parseJSONMap } from "appworks/utils/json-utils";
import * as Logger from "js-logger";
import { TextMetrics } from "pixi.js";
import { Service } from "../service";
import { translationKeyReplacer } from "./translation-key-replacer";

export interface FontSubstitute {
    font: string;
    weight?: string;
    // If specified, substitute if the current language is found in this array
    includedLanguages?: string[];
    // If specified, do NOT substitute if the current language is found in this array
    excludedLanguages?: string[];
    // If specified, only substitute text fields with this instance name
    targetInstances?: string[];
}

export class TranslationsService extends Service {

    private static padding: Map<string, number> = new Map<string, number>([] as any);

    private static metricsStrings: Map<string, string> = new Map<string, string>([
        ["hi", "नि:होतेशुल्कवाइल्ड्सस्पिनपुरस्कारित!"],
        ["vi", "CƯỢCSỐDƯĐđEeÊêĂăÂâỶỷỬửẦầ"]
    ] as any);

    /* This should be a few symbols, one which represents the highest ascend and the lowest descend
     */
    private static baselineSymbols: Map<string, string> = new Map<string, string>([
        ["hi", "ड्सहोतेशु"],
        ["vi", "CƯỢCSỐDƯĐđEeÊêĂăÂâỶỷỬửẦầ"]
    ] as any);

    public debug: boolean = false;

    public formats: { [id: string]: { [id: string]: any } } = {};

    public breakWords: boolean;

    public languageCode: string;

    public allCapsDefault: boolean = true;

    protected translations: Map<string, string>;

    protected aliases: Map<string, string> = new Map();

    protected breakWordLanguages: string[] = ["zh", "ja", "th", "ko", "test"];

    protected pixiToCSS: { [id: string]: string } = {
        fontFamily: "font-family",
        fill: "color",
        fontSize: "font-size"
    };

    protected fontSubstitutes: Map<string, FontSubstitute[]> = new Map();

    public init(): void {
        //
    }

    public setFontSubstitute(font: string, substitute: FontSubstitute) {
        if (!this.fontSubstitutes.has(font)) {
            this.fontSubstitutes.set(font, []);
        }
        this.fontSubstitutes.get(font).push(substitute);
    }

    public getFontSubstitutes(): Map<string, FontSubstitute[]> {
        return this.fontSubstitutes;
    }

    public setLanguage(code: string) {

        if (code.indexOf("-") > -1) {
            code = code.split("-")[0].toLowerCase();
        }

        this.breakWords = false;

        for (const breakWordLanguage of this.breakWordLanguages) {
            if (code.toLocaleLowerCase().indexOf(breakWordLanguage) > -1) {
                this.breakWords = true;
            }
        }

        if (TranslationsService.metricsStrings.has(code)) {
            TextMetrics.METRICS_STRING = TranslationsService.metricsStrings.get(code);
        }

        if (TranslationsService.baselineSymbols.has(code)) {
            TextMetrics.BASELINE_SYMBOL = TranslationsService.baselineSymbols.get(code);
        }

        this.languageCode = code;
    }

    public setAlias(alias: string, code: string) {
        this.aliases.set(alias.toLocaleLowerCase(), code);
    }

    public setFormat(name: string, format: any) {
        this.formats[name] = format;
    }

    public load(json: any) {
        this.translations = parseJSONMap<string>(json.translations);

        lowerCaseMapKeys(this.translations);

        if (json.formats) {
            for (const format in json.formats) {
                if (json.formats.hasOwnProperty(format)) {
                    this.formats[format] = json.formats[format];
                }
            }
        }

        if (this.debug) {
            this.aliases.forEach((alias: string) => {
                this.get(alias);
            });
        }
    }

    public getPadding() {
        if (TranslationsService.padding.has(this.languageCode)) {
            return TranslationsService.padding.get(this.languageCode);
        }

        return 0;
    }

    public set(id: string, translation: string) {
        this.translations.set(id, translation);
    }

    public exists(id: string) {
        const translationId = this.aliases.get(id) || id;
        return this.translations.has(translationId.toLowerCase());
    }

    // Will return first translation which exists, if none exist, first id in list is used
    public getFirstValid(ids: string[], parameters?: { [value: string]: string | number }) {
        let validId = ids.find((id) => this.exists(id));

        if (!validId) {
            validId = ids[0];
        }

        return this.get(validId, parameters);
    }

    public get(id: string, parameters?: { [value: string]: string | number }, capitalize?: boolean): string {
        let translationId = id;

        if (capitalize === undefined) {
            capitalize = this.allCapsDefault;
        }

        if (!translationId) {
            Logger.warn("Trying to load an undefined translation ID.");
            return translationId;
        }

        if (!this.aliases) {
            Logger.warn("Trying to use a translation before they were loaded: " + translationId);
            return translationId;
        }

        const alias = translationId;

        if (!this.exists(translationId)) {
            Logger.warn("Missing translation: " + translationId.toLowerCase());
            return translationId;
        }

        if (this.aliases.has(translationId.toLowerCase())) {
            translationId = this.aliases.get(translationId.toLowerCase());
        }

        translationId = translationId.toLowerCase();

        const rawTranslation: string = this.translations.get(translationId);

        let translation = "";

        // Find brace blocks
        let searchIndex = -1;

        while (true) {

            const blockIndex = rawTranslation.indexOf("{", searchIndex + 1);
            const endBlockIndex = rawTranslation.indexOf("}", blockIndex + 1);

            if (blockIndex === -1) {
                translation += rawTranslation.slice(searchIndex + 1);
                break;
            }

            translation += rawTranslation.slice(searchIndex + 1, blockIndex);

            const block = rawTranslation.slice(blockIndex, endBlockIndex + 1).split(" ").join("");

            const argId = block.slice(1, block.length - 1).toLowerCase();
            let arg: string | number = "";

            if (translationKeyReplacer.lookup(argId)) {
                arg = translationKeyReplacer.get(argId);
            }

            if (parameters) {
                Object.keys(parameters).forEach((key) => {
                    parameters[key.toLowerCase()] = parameters[key];
                });

                if (parameters && parameters[argId] !== undefined) {
                    arg = parameters[argId];
                }
            }

            translation += arg;

            searchIndex = endBlockIndex;
        }

        if (this.debug) {
            translation = "$" + translation + "$";
        }

        if (capitalize) {
            translation = this.toUpperCase(translation);
        }

        if (this.formats && this.formats[translationId]) {
            translation = `<${translationId}>${translation}</${translationId}>`;
        } else if (this.aliases && this.formats[alias]) {
            translation = `<${alias}>${translation}</${alias}>`;
        }

        return translation;
    }

    private toUpperCase(content: string): string {
        // We need to ignore several bracket formats so we just ignore all character between <> angle brackets.
        // Some examples of existing bracket notation that need to be ignored:
        // - <image_file_name />
        // - <start_tag>
        // - </end_tag>

        let isInsideBrackets = false;
        let result = "";

        for (const character of content) {
            if (character === "<") {
                isInsideBrackets = true;
            } else if (character === ">") {
                isInsideBrackets = false;
            }

            if (!isInsideBrackets) {
                result += character.toUpperCase();
            } else {
                result += character;
            }
        }

        return result;
    }

    public updateDOMTexts() {
        const domTexts = document.querySelectorAll(".i18n");
        if (domTexts) {
            for (let i = 0; i < domTexts.length; i++) {
                const domElement = domTexts.item(i) as HTMLElement;
                domElement.innerHTML = this.convertTags(this.get(domElement.innerText || domElement.getAttribute("data-i18n") || ""));

                this.updateFontsWithSubstitute(domElement);
            }
        }
    }

    /**
     * Converts <orange></orange>
     * where orange is defined as
     * {
     *  "fill": "#ff0000",
     *  "fontSize": 100,
     *  "fontFamily": "Arial"
     * }
     *  to <span style="color: #ff0000; font-size: 100%; font-family: Arial"></span>
     * @param text {string}
     */
    public convertTags(text: string): string {
        for (const formatName in this.formats) {
            if (this.formats.hasOwnProperty(formatName)) {
                const format = this.formats[formatName];

                let styleString = "";
                for (const prop in format) {
                    if (format.hasOwnProperty(prop)) {
                        let propName = prop;
                        if (this.pixiToCSS[prop]) {
                            propName = this.pixiToCSS[prop];
                        }
                        let units = "";
                        if (propName === "font-size") {
                            units = "%";
                        }

                        styleString += propName + ": " + format[prop] + units + ";";
                    }
                }

                while (text.indexOf("<" + formatName + ">") > -1) {
                    const tagStart = text.indexOf("<" + formatName + ">");
                    const tagContentsStart = tagStart + formatName.length + 2;
                    const tagContentsEnd = text.indexOf("</" + formatName + ">", tagContentsStart);
                    const tagEnd = tagContentsEnd + formatName.length + 3;

                    const tagContents = text.slice(tagContentsStart, tagContentsEnd);

                    text = text.substring(0, tagStart) + "<span style=\"" + styleString + "\">" + tagContents + "</span>" + text.substr(tagEnd);
                }
            }
        }

        return text;
    }

    public playingInEnglish() {
        return this.languageCode?.toLowerCase().indexOf("en") > -1;
    }

    protected updateFontsWithSubstitute(element: HTMLElement) {
        const language = Model.read().settings.language;

        this.fontSubstitutes.forEach((substitutes: FontSubstitute[], font: string) => {
            substitutes.forEach((substitute: FontSubstitute, index: number) => {
                if (this.elementUsesFont(element, font)) {
                    if (!substitute.targetInstances || (substitute.targetInstances && contains(substitute.targetInstances, element.id))) {
                        if (substitute.includedLanguages && contains(substitute.includedLanguages, language)) {
                            element.style.fontFamily = substitute.font;
                            element.style.fontWeight = substitute.weight;
                        }
                        if (substitute.excludedLanguages && !contains(substitute.excludedLanguages, language)) {
                            element.style.fontFamily = substitute.font;
                            element.style.fontWeight = substitute.weight;
                        }
                    }
                }
            });
        });
    }

    protected elementUsesFont(element: HTMLElement, font: string) {

        const style = document.defaultView.getComputedStyle(element);

        if (style.fontFamily.length > 0) {
            return style.fontFamily.toLowerCase().indexOf(font.toLowerCase()) > -1;
        }

        return false;
    }
}
